import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import CashFlowReportPreview from "../../cash-flow/CashFlowReportPreview.tsx";
import { ReportType } from "../../../../../../../../api/types/svTypes.ts";
import { useBiApiClientProvider } from "../../../../contexts/BiApiClientProviderContext.ts";
import type { BiApiClient } from "../../../../contexts/ApiClientProviderContext.ts";
import { getTabularExportWarning } from "../../../common/export/exportWarnings.ts";
import ExcelReportExport from "../../../common/export/ExcelReportExport.tsx";
import type { GridSortModel } from "@mui/x-data-grid-premium";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useEnhancedBiApiClientProvider } from "../../../../contexts/ApiClientProviderContext.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { selectPreviewMode, selectSessionId } from "../../../../store/selectors/currentReportSelectors.ts";
import { selectLogQueries } from "../../../../store/selectors/devToolsSelectors.ts";
import PreviewActions from "../../../viewer/common/PreviewActions.tsx";
import ViewContainer from "../../../viewer/common/ViewContainer.tsx";
import ViewerParameters from "../../../viewer/common/ViewerParameters.tsx";
import type { TabularRunState } from "../../../viewer/storage/runState.ts";
import useGridStateViewer from "../../../viewer/tabular/hooks/useGridStateViewer.ts";
import { useRequestConfigViewer } from "../../../viewer/tabular/hooks/useRequestConfigViewer.ts";
import PreviewComponent from "../../../viewer/tabular/PreviewComponent.tsx";
import ChangePreviewModeButton from "../../common/ChangePreviewModeButton.tsx";
import useCommonReportHandlers from "../../common/hooks/useCommonReportHandlers.ts";
import { rebuildSortingFields } from "./rebuildSortingFields.ts";

interface Props {
  runState: TabularRunState;
}
export default function TabularViewer({ runState }: Props) {
  const { requestRunTabularExport } = useBiApiClientProvider<BiApiClient>();
  const { runTabularReport, runCashFlowReport } = useEnhancedBiApiClientProvider();
  const previewMode = useSelector(selectPreviewMode);
  const isQueryLoggingActive = useSelector(selectLogQueries);
  const handlers = useCommonReportHandlers(runState.fields);
  const { dimensions, reportId, conditions, onRefreshGrid, onSortingUpdate } = useBiCommonDataContext();
  const sessionId = useSelector(selectSessionId);

  const { requestConfig: viewConfig } = useRequestConfigViewer({
    fields: runState.fields,
    sorting: runState.sort,
    conditions: conditions,
    reportId: reportId,
    sessionId: sessionId,
    isQueryLoggingActive: isQueryLoggingActive,
  });

  const gridState = useGridStateViewer({
    fields: runState.fields,
    sorting: runState.sort,
    requestConfig: viewConfig,
    setEtag: handlers.setEtag,
    setResponse: undefined,
    updateDevTools: handlers.updateDevTools,
    runReport: runState.reportType === ReportType.CashFlow ? defined(runCashFlowReport) : runTabularReport,
  });

  const onColumnsSortingChange = useCallback(
    (newSorting: GridSortModel) => {
      const sortFields = rebuildSortingFields(newSorting, runState.fields, runState.sort);
      onSortingUpdate?.(sortFields);
    },
    [runState.fields, runState.sort, onSortingUpdate]
  );
  const cashFlow = runState.reportType === ReportType.CashFlow;

  return (
    <>
      {!cashFlow && (
        <ExcelReportExport
          mode="run"
          requestConfig={viewConfig}
          dimensions={dimensions}
          requestExport={requestRunTabularExport}
          warning={getTabularExportWarning(gridState)}
        />
      )}
      <ViewContainer
        Parameters={
          runState.showParameters ? (
            <ViewerParameters
              fieldsWithConditions={runState.fieldsWithCustomConditionParameters}
              initialDetailedView={runState.settings.detailedView}
            />
          ) : (
            <></>
          )
        }
        Preview={
          cashFlow ? (
            <CashFlowReportPreview
              gridState={gridState}
              fields={runState.fields}
              settings={runState.settings}
              exportSource={{
                mode: "run",
                requestConfig: viewConfig,
                dimensions,
                requestExport: requestRunTabularExport,
              }}
            />
          ) : (
            <PreviewComponent
              gridState={gridState}
              onRefresh={onRefreshGrid}
              //Disabled due to view mode
              disableColumnManagement={true}
              fields={runState.fields}
              groups={runState.grouping}
              previewMode={previewMode}
              settings={runState.settings}
              sorts={runState.sort}
              onColumnsSortingChanged={onColumnsSortingChange}
              onDrillDown={handlers.onDrillDown}
              onPredefinedCustomFieldType={undefined}
            />
          )
        }
        ActionPanel={<PreviewActions onRefresh={onRefreshGrid} ChangePreviewModeButton={<ChangePreviewModeButton />} />}
      />
    </>
  );
}
